import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { Typography } from 'antd';
import { AccessibleButton } from './AccessibleButton';
import { AccessibleImage } from './AccessibleImage';
import type { MissionWithRelations, ParcoursWithRelations } from '../types/database';
import { navigationItems, handleAnchorClick, isCurrentPage } from '../utils/navigation';

const { Title, Paragraph } = Typography;

interface MissionModalProps {
  hidden:any;
  setHidden:any;
  isOpen: boolean;
  isSuccess: boolean;
  mission: MissionWithRelations;
  parcours: ParcoursWithRelations;
  currentMissionIndex: number;
  onClose?: () => void;
}

export const MissionModal: React.FC<MissionModalProps> = ({
  hidden,
  setHidden,
  isOpen,
  isSuccess,
  mission,
  parcours,
  currentMissionIndex,
  onClose
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const hasNextMission = currentMissionIndex < parcours.missions.length - 1;
  
  const handleNextMission = () => {
    if (hasNextMission) {
      setHidden(true)
      const nextMission = parcours.missions[currentMissionIndex + 1];
      navigate(`/parcours/${parcours.slug}/mission/${nextMission.id}`);
    } else {
      // Aller au parcours suivant (à implémenter selon la logique métier)
      navigate('/#parcours');
    }
    if (onClose) onClose();
  };

  const handleViewParcoursSheet = () => {
    // Navigation vers la fiche parcours
    navigate(`/parcours/${parcours.slug}/fiche`);
    if (onClose) onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 300,
              duration: 0.5
            }}
            className="relative w-full max-w-4xl mx-4 bg-white rounded-2xl shadow-2xl border-0" 
          >
            {/* Contenu de la modale */}
            <div className="p-8">
              {/* Haut : Avatar + Titre */}
              <div className="flex items-start gap-6 mb-2">
                {/* Avatar Andy K avec bulle colorée */}
                <div className="flex-shrink-0 absolute -left-[30px] -top-[20px]">
                  <div 
                    className={`w-24 h-24 rounded-full border-4 flex items-center justify-center bg-white ${
                      isSuccess ? 'border-green-500' : 'border-red-500'
                    }`}
                  >
                    <AccessibleImage
                      src={isSuccess ? '/img/mascottes/andy-k-avatar.png' : '/img/mascottes/andy-k-avatar.png'}
                      altKey="images.mascotteAvatar"
                      className="h-full w-full rounded-full"
                    />
                  </div>
                </div>

                {/* Titre et sous-titre */}
                <div className="flex-1">
                  <Title level={2} className="ml-12 !text-3xl !font-bold !mb-4 !text-neutral-black">
                    {mission.solution[(isSuccess)?"sucess":"failed"].title}
                  </Title>
                  <Paragraph className="!text-lg !text-neutral-600 !mb-0">
                    {mission.solution[(isSuccess)?"sucess":"failed"].subtitle}
                  </Paragraph>
                </div>
              </div>

              {/* Explication de la solution */}
              <div className="mb-6">
                {/* <Paragraph className="!text-lg !leading-relaxed !text-neutral-700">
                  {mission.solution.explicationCourte.replaceAll('\n', '<br> • ')}
                </Paragraph> */}
                <p className="!text-lg !leading-relaxed !text-neutral-700" dangerouslySetInnerHTML={{ __html: mission.solution.explicationCourte?.replaceAll('\n', '<br>') }}/>
                
                {mission.solution.explicationDetaillee && (
                  <p className="!text-lg !leading-relaxed !text-neutral-700" dangerouslySetInnerHTML={{ __html: mission.solution.explicationDetaillee?.replaceAll('\n-', '<br> • ')?.replaceAll('\n', '<br>') }}/>
                )}
              </div>

              {/* Image d'explication (couverture) */}
              {mission.solution.couvertures && mission.solution.couvertures[0] && (
                <div className="mb-8">
                  <div className="rounded-lg">
                    <AccessibleImage
                      src={mission.solution.couvertures[0]}
                      altKey="images.solutionCover"
                      className="object-cover"
                    />
                  </div>
                </div>
              )}

              {/* Zone grise placeholder si pas d'image */}
              {/* {(!mission.solution.couvertures || !mission.solution.couvertures[0]) && (
                <div className="w-full h-64 bg-neutral-200 rounded-lg mb-8 flex items-center justify-center">
                  <span className="text-neutral-500">Image d'explication à venir</span>
                </div>
              )} */}

              {/* Boutons d'action */}
              <div className="flex flex-wrap gap-8">
                {/* Bouton principal : Mission suivante ou Parcours suivant */}
                <AccessibleButton
                  type="primary"
                  onClick={handleNextMission}
                  className="flex min-w-max" 
                >
                  {hasNextMission 
                    ? "Passer à la mission suivante" 
                    : "Passer au parcours suivant"
                  }
                </AccessibleButton>

                {/* Voir la fiche parcours */}
                <AccessibleButton
                  type="secondary"
                  onClick={handleViewParcoursSheet}
                  className="flex min-w-max"
                >
                  Voir la fiche parcours
                </AccessibleButton>
                
                {/* Retour aux parcours */}
                <AccessibleButton
                  isLink={ true }
                  type="link"
                  isUnderlined={true}
                  className="min-w-max"
                  style={{ textDecoration : "none" }}
                  href="#parcours"
                >
                  Retours aux parcours
                </AccessibleButton>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};